import type { AxiosError } from 'axios'

import { useMemo, useState, useEffect } from 'react'

import { getIdToken } from '../../../lib/cognito'
import DocumentModal from '../../shared/DocumentViewer/modal'
import { getDocument, getImage } from '../actions'

import ImagePreviewer from './image-preview'

interface MessageAttachmentProps {
  chatId: string
  currentUserId: string
  patientId: string
  attachment: {
    id: string
    mime_type: string
    url: string
    size: bigint
    filename: string
  }
  clientName: string
  rounded: boolean
}

export function MessageAttachment ({
  chatId,
  currentUserId,
  patientId,
  attachment,
  clientName,
  rounded
}: MessageAttachmentProps): JSX.Element | null {
  const [ openModal, setOpenModal ] = useState<boolean>(false)
  const [ preview, setPreview ] = useState<{ data?: string }>({})
  const [ error, setError ] = useState<AxiosError | null>(null)
  const participantId = `Practitioner/${currentUserId}`
  const { url, size, filename, mime_type: mimeType, id } = attachment
  const attachmentId = url.split('/')[2]
  const DOWNLOAD_URL = `/api/patients/${patientId}/messages/${chatId}/attachment/${id}?participant=${participantId}&clientName=${clientName}&downloadType=attachment`

  const meta = useMemo(() => ({
    participantId,
    id: attachmentId,
    chatId,
    size,
    filename,
    mime_type: mimeType
  }), [ attachmentId, chatId, filename, mimeType, participantId, size ])

  useEffect(() => {
    if (mimeType === 'application/pdf') {
      getDocument(
        patientId,
        chatId,
        attachmentId,
        meta,
        clientName,
        'MessageAttachment'
      )?.then((response: { data: string }) => setPreview(response))
        .catch((e: AxiosError) => {
          setError(e)
          console.error({ e })
        })
    }

    getImage(patientId, chatId, attachmentId, meta, clientName)
      ?.then((response: { data: string }) => setPreview(response))
      .catch((e: AxiosError) => {
        setError(e)
        console.error({ e })
      })
  }, [ attachmentId, attachment, chatId, clientName, meta, mimeType, patientId ])

  const handleOpen = (): void => {
    setOpenModal(true)
  }

  const handleClose = (): void => {
    setOpenModal(false)
  }

  const openUrlInNewTab = (): void =>
    getIdToken().then((idToken: string) => {
      window.open(`${DOWNLOAD_URL}&authorization=Bearer ${idToken}`, '_blank')
    })

  const Modal = (): JSX.Element | null => {
    return (
      <DocumentModal
        meta={meta}
        open={openModal}
        close={handleClose}
      />
    )
  }

  if (error) {
    return null
  }

  const borderRadius = rounded ? '16px 16px 0 0' : 'none'

  return (
    <>
      {openModal && <Modal />}
      <div
        className='attachment'
        style={{ borderRadius }}
      >
        <div className='document-viewer'>
          <ImagePreviewer
            data={preview?.data}
            handleClick={handleOpen}
            handleOpenInNewTab={openUrlInNewTab}
            url={DOWNLOAD_URL}
            meta={meta}
          />
        </div>
      </div>
    </>
  )
}
